import { supabase } from '@/lib/supabase';
import { ShoeItem, ShoeItemFormData } from '@/models/types';
import { uploadFile } from '@/lib/uploadFile';
import { getProxiedImageUrl } from '@/services/imageProxyService';

export interface ShoeFilterOptions {
  category?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  searchTerm?: string;
  itemId?: number;
  sortOption?: string;
}

export const getShoeItems = async (page = 1, perPage = 10, filters: ShoeFilterOptions = {}): Promise<{ data: ShoeItem[]; count: number }> => {
  let query = supabase
    .from('shoes')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.category) {
    query = query.eq('category[0]', filters.category as any);
  }
  
  if (filters.brand) {
    query = query.eq('brand', filters.brand as any);
  }
  
  if (filters.minPrice !== undefined) {
    query = query.gte('price', filters.minPrice);
  }
  
  if (filters.maxPrice !== undefined) {
    query = query.lte('price', filters.maxPrice);
  }
  
  if (filters.itemId !== undefined) {
    query = query.eq('_id', filters.itemId as any);
  }
  
  if (filters.searchTerm) {
    query = query.or(`name.ilike.%${filters.searchTerm}%,category[0].ilike.%${filters.searchTerm}%`);
  }

  // Apply sorting
  if (filters.sortOption) {
    const [field, direction] = filters.sortOption.split('_');
    if (field === 'id') {
      query = query.order('_id', { ascending: direction === 'asc' });
    } else {
      query = query.order(field, { ascending: direction === 'asc' });
    }
  } else {
    // Default sorting by ID ascending (smallest to highest)
    query = query.order('_id', { ascending: true });
  }

  // Apply pagination
  const start = (page - 1) * perPage;
  query = query.range(start, start + perPage - 1);

  const { data, error, count } = await query;

  if (error) throw error;
  return { 
    data: (data || []) as unknown as ShoeItem[], 
    count: count || 0 
  };
};

export const getShoeItem = async (id: number): Promise<ShoeItem> => {
  const { data, error } = await supabase
    .from('shoes')
    .select('*')
    .eq('_id', id as any)
    .single();

  if (error) throw error;
  return data as unknown as ShoeItem;
};

export const checkShoeItemDuplicate = async (link: string): Promise<boolean> => {
  // Skip checking if link is empty
  if (!link || link.trim() === '') return false;
  
  // Remove any protocol prefixes for more accurate matching
  const cleanLink = link.replace(/^https?:\/\//i, '');
  
  const { data, error } = await supabase
    .from('shoes')
    .select('_id')
    .ilike('link', `%${cleanLink}%`)
    .maybeSingle();
  
  if (error) {
    console.error('Error checking for duplicate link in shoes table:', error);
    return false;
  }
  
  // Return true if data exists (duplicate found)
  return !!data;
};

export const createShoeItem = async (formData: ShoeItemFormData): Promise<ShoeItem | { error: string; isDuplicate?: boolean }> => {
  // Check for duplicate link only in the shoes table
  if (formData.link) {
    const isDuplicate = await checkShoeItemDuplicate(formData.link);
    if (isDuplicate) {
      return { error: 'Duplicate link detected in shoes database', isDuplicate: true };
    }
  }

  // First, get the highest _id currently in the table
  const { data: maxIdData, error: maxIdError } = await supabase
    .from('shoes')
    .select('_id')
    .order('_id', { ascending: false })
    .limit(1);

  if (maxIdError) {
    console.error('Error fetching max ID:', maxIdError);
    throw maxIdError;
  }

  // Calculate the next ID
  const nextId = maxIdData && maxIdData.length > 0 ? (maxIdData[0]._id + 1) : 1;

  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string' && formData.image) {
    // Proxy the image URL only if it's not empty
    imageUrl = await getProxiedImageUrl(formData.image);
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  const insertData = {
    _id: nextId,
    name: formData.name,
    brand: 'none', // Always set to 'none'
    price: formData.price,
    image: imageUrl,
    link: formData.link,
    "category[0]": formData["category[0]"],
    "category[1]": formData["category[1]"],
    "category[2]": formData["category[2]"],
    batch: formData.batch,
    created_by: user?.email || 'Unknown user',
    created_at: timestamp,
    updated_at: timestamp
  } as any;

  const { data, error } = await supabase
    .from('shoes')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ShoeItem;
};

export const updateShoeItem = async (id: number, formData: ShoeItemFormData): Promise<ShoeItem> => {
  let imageUrl: string | undefined;
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Always proxy the image URL
    imageUrl = await getProxiedImageUrl(formData.image);
  }

  const timestamp = new Date().toISOString();

  const updateData = {
    name: formData.name,
    brand: 'none', // Always set to 'none'
    price: formData.price,
    link: formData.link,
    "category[0]": formData["category[0]"],
    "category[1]": formData["category[1]"],
    "category[2]": formData["category[2]"],
    batch: formData.batch,
    updated_at: timestamp,
    ...(imageUrl ? { image: imageUrl } : {})
  } as any;

  const { data, error } = await supabase
    .from('shoes')
    .update(updateData)
    .eq('_id', id as any)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ShoeItem;
};

export const deleteShoeItem = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('shoes')
    .delete()
    .eq('_id', id as any);

  if (error) throw error;
};
